import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, BehaviorSubject, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { AuthenticationService } from '../authentication/authentication.service';
import { CredentialToken } from '../authentication/models';
import { NotificationService } from '../services/notification.service';
import { JWT_INTERCEPTOR_HANDLES_401 } from './error.interceptor';

/**
 * Tracks whether a token refresh is in progress.
 * Using module-level state to handle concurrent requests during refresh.
 */
let refreshing = false;
const refreshTokenSubject = new BehaviorSubject<string | null>(null);

/**
 * URLs that should always include credentials (for cookie-based auth).
 */
const CREDENTIAL_URLS = ['api/auth/refresh', 'api/auth/logout'];

const SESSION_EXPIRED_MESSAGE =
  'Access denied or your session has expired. Please reload the page or sign in again.';

/**
 * URLs that should skip the JWT interceptor entirely.
 * These are public endpoints that don't need authentication.
 */
const SKIP_AUTH_URLS = [
  'api/security/login',
  'api/security/rater/login',
  'api/security/password-reset/request',
  'api/security/password-reset/complete',
];

/**
 * Adds the Authorization header with Bearer token to the request.
 * Also ensures withCredentials is set for API requests to support HttpOnly cookies.
 */
function addToken(
  request: HttpRequest<unknown>,
  token: string,
  jwtHandles401 = true,
): HttpRequest<unknown> {
  return request.clone({
    headers: request.headers.set('Authorization', `Bearer ${token}`),
    context: request.context.set(JWT_INTERCEPTOR_HANDLES_401, jwtHandles401),
    withCredentials: true, // Send HttpOnly cookies with requests
  });
}

/**
 * Clones a request with withCredentials enabled.
 */
function addCredentials(request: HttpRequest<unknown>): HttpRequest<unknown> {
  return request.clone({
    withCredentials: true,
  });
}

/**
 * Handles 401 Unauthorized errors by attempting to refresh the token.
 * Coordinates multiple concurrent requests to prevent race conditions.
 * Uses HttpOnly refresh token cookie for secure token refresh.
 */
function handle401Error(
  request: HttpRequest<unknown>,
  next: HttpHandlerFn,
  authService: AuthenticationService,
  notificationService: NotificationService,
): Observable<HttpEvent<unknown>> {
  if (!refreshing) {
    refreshing = true;
    refreshTokenSubject.next(null);

    return authService.refreshToken().pipe(
      catchError((error: unknown) => {
        refreshing = false;
        notificationService.showServerError(SESSION_EXPIRED_MESSAGE);
        return throwError(() => error);
      }),
      switchMap((token: CredentialToken) => {
        refreshing = false;
        if (token.access_token) {
          refreshTokenSubject.next(token.access_token);
          return next(addToken(request, token.access_token, false));
        }
        return throwError(() => new Error('Token refresh failed'));
      }),
    );
  }

  // Another request is already refreshing the token, wait for it
  return refreshTokenSubject.pipe(
    filter((token): token is string => token !== null),
    take(1),
    switchMap((token) => next(addToken(request, token))),
  );
}

/**
 * Checks if a URL should skip authentication handling.
 */
function shouldSkipAuth(url: string): boolean {
  return SKIP_AUTH_URLS.some((skipUrl) => url.includes(skipUrl));
}

/**
 * Checks if a URL should include credentials (for cookie-based auth).
 */
function shouldIncludeCredentials(url: string): boolean {
  return CREDENTIAL_URLS.some((credUrl) => url.includes(credUrl));
}

/**
 * JWT interceptor that adds the Authorization header to outgoing requests
 * and handles 401 errors by refreshing the token using HttpOnly cookies.
 *
 * Security features:
 * - Access token stored in memory only (via AuthenticationService signal)
 * - Refresh token stored in HttpOnly cookie (browser handles automatically)
 * - withCredentials enabled for API requests to send cookies
 * - Automatic token refresh on 401 responses
 * - Concurrent request handling during refresh
 *
 * This is a functional interceptor following Angular 21 patterns.
 */
export const jwtInterceptor: HttpInterceptorFn = (
  request: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> => {
  const authService = inject(AuthenticationService);
  const notificationService = inject(NotificationService);

  // Skip auth handling for login endpoints
  if (shouldSkipAuth(request.url)) {
    return next(addCredentials(request));
  }

  // For refresh/logout endpoints, just add credentials
  if (shouldIncludeCredentials(request.url)) {
    return next(addCredentials(request));
  }

  const token = authService.getAccessToken();

  // Add token to request if available
  if (token) {
    request = addToken(request, token);
  } else {
    // No token but still include credentials for cookie-based auth
    request = addCredentials(request);
  }

  return next(request).pipe(
    catchError((error: unknown) => {
      if (error instanceof HttpErrorResponse && error.status === 401) {
        // Don't try to refresh for refresh endpoint failures
        if (request.url.includes('api/auth/refresh')) {
          return throwError(() => error);
        }
        return handle401Error(request, next, authService, notificationService);
      }
      return throwError(() => error);
    }),
  );
};
